
import { select, subscribe } from '@wordpress/data';
import { cartStore } from '@woocommerce/block-data';




// Wait for WordPress and WooCommerce to be available
document.addEventListener('DOMContentLoaded', function () {


    // Check if required globals are available
    if (typeof wp === 'undefined' || typeof wp.plugins === 'undefined') {
        //console.warn('Fishouflage: wp.plugins not available, skipping cart plugin registration');
        return;
    }

    if (typeof wc === 'undefined' || typeof wc.blocksCheckout === 'undefined') {
        //console.warn('Fishouflage: wc.blocksCheckout not available, skipping cart plugin registration');
        return;
    }

    const { registerPlugin } = wp.plugins;
    const { ExperimentalOrderMeta } = wc.blocksCheckout;


    const ContinueShoppingComponent = ({ cart, extensions }) => {
        const handleContinueShopping = () => {
            window.location.href = '/';
        };

        return (
            <div className="fishouflage-continue-shopping">
                <button
                    type="button"
                    className="button continue-shopping-btn"
                    onClick={handleContinueShopping}
                >
                    Continue Shopping
                </button>
            </div>
        );
    };

    const render = () => {
        return (
            <ExperimentalOrderMeta>
                <ContinueShoppingComponent />
            </ExperimentalOrderMeta>
        );
    };

    try {
        registerPlugin('fishouflage', {
            render,
            scope: 'woocommerce-checkout',
        });
        console.log('Fishouflage: Cart plugin registered successfully');

    } catch (error) {
        //console.error('Fishouflage: Error registering cart plugin:', error);
    }

    // Update free shipping threshold bar when cart changes
    if (typeof wp !== 'undefined' && wp.data && wp.data.subscribe) {
        let previousCartTotal = null;

        // Initial check on page load
        if (typeof wc !== 'undefined' && wc.wcBlocksData) {
            const store = wp.data.select(wc.wcBlocksData.CART_STORE_KEY);
            if (store) {
                const cartData = store.getCartData();
                if (cartData?.totals) {
                    updateFreeShippingBar(cartData);
                    previousCartTotal = cartData.totals.total_items || 0;
                }
            }
        }

        wp.data.subscribe(() => {
            if (typeof wc !== 'undefined' && wc.wcBlocksData) {
                const store = wp.data.select(wc.wcBlocksData.CART_STORE_KEY);
                if (store) {
                    const cartData = store.getCartData();
                    const currentTotal = cartData?.totals?.total_items || 0;

                    // Only update if total has changed
                    if (currentTotal !== previousCartTotal) {
                        previousCartTotal = currentTotal;
                        updateFreeShippingBar(cartData);
                    }
                }
            }
        });
    }

});

/**
 * Get free shipping threshold from current page payload.
 * Falls back to the value embedded in the progress bar DOM if needed,
 * then to 60 as a final safety default.
 */
function getFreeShippingThreshold() {
    const progressBar = document.querySelector('.fishouflage-cart-shipping-progress[data-free-shipping-threshold]');
    if (progressBar) {
        const dataThreshold = parseFloat(progressBar.dataset.freeShippingThreshold);
        if (Number.isFinite(dataThreshold) && dataThreshold > 0) {
            return dataThreshold;
        }
    }

    const globalThreshold = window.fishouflageCart?.freeShippingThreshold;

    if (typeof globalThreshold === 'number' && Number.isFinite(globalThreshold) && globalThreshold > 0) {
        return globalThreshold;
    }

    if (typeof globalThreshold === 'string') {
        const parsedThreshold = parseFloat(globalThreshold);
        if (Number.isFinite(parsedThreshold) && parsedThreshold > 0) {
            return parsedThreshold;
        }
    }

    return 60;
}

/**
 * Update the free shipping threshold bar
 */
function updateFreeShippingBar(cartData) {
    const progressBar = document.querySelector('.fishouflage-cart-shipping-progress');
    if (!progressBar) return;

    // Get cart total (in cents, need to convert to dollars)
    const cartTotal = parseInt(cartData.totals.total_items) / Math.pow(10, cartData.totals.currency_minor_unit);

    const freeShippingThreshold = getFreeShippingThreshold();

    // Calculate progress
    let progress = (cartTotal / freeShippingThreshold) * 100;
    const remaining = freeShippingThreshold - cartTotal;

    // Check if shipping is free
    const shippingTotal = parseInt(cartData.totals.total_shipping);
    if (shippingTotal === 0 && cartTotal > 0) {
        progress = 100;
    }

    // Cap at 100%
    if (progress > 100) {
        progress = 100;
    }

    // Update message
    const messageEl = progressBar.querySelector('.shipping-progress p');
    if (messageEl) {
        if (remaining > 0) {
            messageEl.textContent = `You are $${remaining.toFixed(2)} away from Free Shipping!`;
        } else {
            messageEl.textContent = 'You have qualified for Free Shipping!';
        }
    }

    // Update progress bar
    const progressSpan = progressBar.querySelector('.progress-bar span');
    if (progressSpan) {
        progressSpan.style.width = progress + '%';
    }

    // Ensure subtotal row is always visible
    ensureSubtotalVisible(cartData);
}

/**
 * Ensure the subtotal row is always visible in the cart totals
 *
 * WooCommerce blocks hides subtotal when ALL of these conditions are true:
 * - total_fees = 0
 * - total_discount = 0
 * - total_shipping = 0 (free shipping)
 *
 * This function injects a subtotal row when WooCommerce hides it.
 */
function ensureSubtotalVisible(cartData) {
    // Find the totals wrapper - this is where we need to inject
    const totalsWrapper = document.querySelector('.wp-block-woocommerce-cart-order-summary-totals-block');
    if (!totalsWrapper) return;

    // Check if NATIVE subtotal block exists (not our injected one)
    // Native WooCommerce subtotal won't have our fishouflage-subtotal-row class
    const nativeSubtotal = totalsWrapper.querySelector(
        '.wp-block-woocommerce-cart-order-summary-subtotal-block:not(.fishouflage-subtotal-row)'
    );
    const existingFishouflageSubtotal = totalsWrapper.querySelector('.fishouflage-subtotal-row');

    // Get subtotal value from cart data
    const subtotalValue = parseInt(cartData.totals.total_items || 0) / Math.pow(10, cartData.totals.currency_minor_unit || 2);
    const currencySymbol = cartData.totals.currency_symbol || '$';
    const formattedSubtotal = currencySymbol + subtotalValue.toFixed(2);

    // If native subtotal exists, remove our injected one and let WooCommerce handle it
    if (nativeSubtotal) {
        if (existingFishouflageSubtotal) {
            existingFishouflageSubtotal.remove();
        }
        return;
    }

    // Native subtotal doesn't exist - WooCommerce is hiding it
    // We need to inject our own
    if (existingFishouflageSubtotal) {
        // Already have our injected subtotal, just update the value
        const valueEl = existingFishouflageSubtotal.querySelector('.wc-block-components-totals-item__value');
        if (valueEl && valueEl.textContent !== formattedSubtotal) {
            valueEl.textContent = formattedSubtotal;
        }
        return;
    }

    // Need to create and inject subtotal row
    // Find the shipping row to insert before it
    const shippingRow = totalsWrapper.querySelector(
        '.wp-block-woocommerce-cart-order-summary-shipping-block'
    );

    if (shippingRow) {
        // Create subtotal row matching WooCommerce blocks structure
        // Note: We use fishouflage-subtotal-row class to identify our injected element
        const subtotalRow = document.createElement('div');
        subtotalRow.className = 'fishouflage-subtotal-row';
        subtotalRow.innerHTML = `
            <div class="wc-block-components-totals-wrapper">
                <div class="wc-block-components-totals-item wc-block-components-totals-subtotal">
                    <span class="wc-block-components-totals-item__label">Subtotal</span>
                    <span class="wc-block-components-totals-item__value">${formattedSubtotal}</span>
                </div>
            </div>
        `;

        // Insert before shipping row
        shippingRow.parentNode.insertBefore(subtotalRow, shippingRow);
    }
}

/**
 * Watch for DOM changes to catch React re-renders and ensure subtotal stays visible
 *
 * WooCommerce Cart Block uses React and re-renders when cart data changes.
 * The subtotal block is conditionally rendered based on:
 * !parseFloat(total_fees) && !parseFloat(total_discount) && !parseFloat(total_shipping)
 *
 * When this condition is true (free shipping, no fees, no discounts), the subtotal
 * block returns null and is removed from the DOM. We need to watch for this and
 * inject our own subtotal row.
 */
function watchForCartTotals() {
    if (typeof MutationObserver === 'undefined') return;

    let debounceTimer = null;

    const checkSubtotal = () => {
        // Get cart data and ensure subtotal is visible
        if (typeof wp !== 'undefined' && typeof wc !== 'undefined' && wc.wcBlocksData) {
            const store = wp.data.select(wc.wcBlocksData.CART_STORE_KEY);
            if (store) {
                const cartData = store.getCartData();
                if (cartData?.totals) {
                    ensureSubtotalVisible(cartData);
                }
            }
        }
    };

    const debouncedCheck = () => {
        if (debounceTimer) clearTimeout(debounceTimer);
        debounceTimer = setTimeout(checkSubtotal, 200); // 200ms debounce to prevent flickering
    };

    // Watch for changes in the cart totals area specifically
    const observer = new MutationObserver((mutations) => {
        // Only react to changes in the totals block, not our own injections
        let shouldCheck = false;

        for (const mutation of mutations) {
            if (mutation.type === 'childList') {
                // Check if a native subtotal was added or removed (not our injected one)
                const addedNodes = Array.from(mutation.addedNodes);
                const removedNodes = Array.from(mutation.removedNodes);

                for (const node of [...addedNodes, ...removedNodes]) {
                    if (node.nodeType === Node.ELEMENT_NODE) {
                        // If WooCommerce's native subtotal was added/removed, we should check
                        if (node.classList?.contains('wp-block-woocommerce-cart-order-summary-subtotal-block') &&
                            !node.classList?.contains('fishouflage-subtotal-row')) {
                            shouldCheck = true;
                            break;
                        }
                    }
                }

                if (shouldCheck) break;
            }
        }

        if (shouldCheck) {
            debouncedCheck();
        }
    });

    // Start observing when the cart block area exists
    const startObserving = () => {
        const totalsBlock = document.querySelector('.wp-block-woocommerce-cart-order-summary-totals-block');

        if (totalsBlock) {
            observer.observe(totalsBlock, {
                childList: true,
                subtree: true
            });
            // Initial check
            checkSubtotal();
        } else {
            // Totals block not yet rendered, wait and retry
            setTimeout(startObserving, 200);
        }
    };

    startObserving();

    // Run check a few times after page load to catch initial render
    setTimeout(checkSubtotal, 500);
    setTimeout(checkSubtotal, 1000);
    setTimeout(checkSubtotal, 2000);
}

// Start watching for cart totals on DOMContentLoaded
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', watchForCartTotals);
} else {
    watchForCartTotals();
}

